import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const BATCH_DETAIL_FIELDS = [
  { key: 'id', label: 'Entry ID' },
  { key: 'product', label: 'Product ID' },
  { key: 'product_name', label: 'Product Name' },
  { key: 'product_sku', label: 'Product SKU' },
  { key: 'branch', label: 'Branch ID' },
  { key: 'branch_name', label: 'Branch Name' },
  { key: 'warehouse_name', label: 'Warehouse' },
  { key: 'supplier', label: 'Supplier ID' },
  { key: 'supplier_name', label: 'Supplier Name' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'reserved_quantity', label: 'Reserved Quantity' },
  { key: 'available_quantity_for_sale', label: 'Available for Sale' },
  { key: 'purchase_price', label: 'Purchase Price' },
  { key: 'selling_price', label: 'Selling Price' },
  { key: 'total_cost', label: 'Total Cost' },
  { key: 'batch_number', label: 'Batch Number' },
  { key: 'original_batch_number', label: 'Original Batch Number' },
  { key: 'received_date', label: 'Received Date' },
  { key: 'notes', label: 'Notes' },
  { key: 'is_initial_stock', label: 'Initial Stock' },
  { key: 'source_transfer', label: 'Transfer ID' },
  { key: 'source_transfer_reference', label: 'Transfer Reference' },
  { key: 'original_stock_entry', label: 'Original Stock Entry' },
  { key: 'original_stock_entry_id', label: 'Original Stock Entry ID' },
  { key: 'original_branch_stock', label: 'Original Branch Stock' },
  { key: 'created_by', label: 'Created By (ID)' },
  { key: 'created_by_email', label: 'Created By (Email)' },
  { key: 'created_at', label: 'Created At' }
]

const parseBranchStockResponse = (data) => {
  if (Array.isArray(data)) {
    return data
  }
  if (Array.isArray(data?.results)) {
    return data.results
  }
  return []
}

const BranchStock = ({ branch }) => {
  const [stockEntries, setStockEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [valueSummary, setValueSummary] = useState(null)
  const branchStockRef = useRef(null)

  const hasBranchContext = !!branch?.id

  const handleGoBack = () => {
    const event = new CustomEvent('navigateToScreen', {
      detail: { screen: 'manage-branches' }
    })
    window.dispatchEvent(event)
  }

  const showEntryDetails = (entry) => {
    if (!entry) {
      Swal.fire({
        icon: 'info',
        title: 'Stock Entry',
        text: 'No entry details available.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    const detailRows = BATCH_DETAIL_FIELDS.map(field => {
      const value = formatBatchDetailValue(field.key, entry[field.key])
      return `
        <div style="display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px solid #eef2f7;">
          <span style="font-weight: 600; color: #5d6d7e;">${field.label}</span>
          <span style="color: #2c3e50; text-align: right; flex: 1;">${value}</span>
        </div>
      `
    }).join('')

    Swal.fire({
      title: `<span style="font-size: 18px; font-weight: 600; color: #2c3e50;">${entry.product_name || 'Stock Entry'}</span>`,
      html: `
        <div style="text-align: left; max-height: 60vh; overflow-y: auto;">
          <div style="padding: 14px; border-radius: 10px; background: #f8fafc; border: 1px solid #e1e8f0;">
            <div style="font-weight: 600; color: #1f3a93; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
              <span>${entry.batch_number || entry.original_batch_number || 'Batch Entry'}</span>
              <span style="font-size: 13px; color: #5d6d7e;">Qty: <strong>${parseFloat(entry.quantity || 0).toLocaleString()}</strong></span>
            </div>
            ${detailRows}
          </div>
        </div>
      `,
      width: 500,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6'
    })
  }

  const fetchBranchStock = useCallback(async () => {
    if (!branch?.id) {
      setLoading(false)
      setError('No branch context found. Please return and select a branch again.')
      return
    }

    try {
      setLoading(true)
      setError(null)
      const [stockResult, valueResult] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.BRANCH_STOCK, { params: { branch_id: branch.id } }),
        apiClient.get(API_ENDPOINTS.BRANCH_STOCK_VALUE, { params: { branch_id: branch.id } })
      ])

      if (stockResult.status === 'rejected') {
        throw stockResult.reason
      }

      setStockEntries(parseBranchStockResponse(stockResult.value.data))

      if (valueResult.status === 'fulfilled') {
        setValueSummary(valueResult.value.data || null)
      } else {
        setValueSummary(null)
        console.warn('Branch stock value summary failed to load:', valueResult.reason)
      }

      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error fetching branch stock:', err)
      const message = err.response?.data?.detail || err.response?.data?.message || 'Failed to load branch stock. Please try again.'
      setError(message)
      Swal.fire({
        icon: 'error',
        title: 'Branch Stock',
        text: message,
        confirmButtonColor: '#d33'
      })
    } finally {
      setLoading(false)
    }
  }, [branch?.id])

  useEffect(() => {
    fetchBranchStock()
  }, [fetchBranchStock])

  const summary = useMemo(() => {
    const productIds = new Set(stockEntries.map(entry => entry.product).filter(Boolean))
    const totalProducts = productIds.size
    const totalUnits = stockEntries.reduce((sum, entry) => sum + (parseFloat(entry.quantity) || 0), 0)
    const totalValue = stockEntries.reduce((sum, entry) => {
      const entryCost =
        parseFloat(entry.total_cost) ||
        (parseFloat(entry.quantity) || 0) * (parseFloat(entry.purchase_price) || 0)
      return sum + entryCost
    }, 0)

    return {
      totalProducts,
      totalUnits,
      totalValue
    }
  }, [stockEntries])

  const filteredStock = useMemo(() => {
    if (!search) return stockEntries

    const term = search.toLowerCase()
    return stockEntries.filter(entry => {
      return (
        entry.product_name?.toLowerCase().includes(term) ||
        entry.product_sku?.toLowerCase().includes(term) ||
        entry.batch_number?.toLowerCase().includes(term) ||
        entry.original_batch_number?.toLowerCase().includes(term) ||
        entry.source_transfer_reference?.toLowerCase().includes(term)
      )
    })
  }, [search, stockEntries])

  const {
    paginatedItems: paginatedStock,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredStock)

  const formatCurrency = (value) => {
    const number = parseFloat(value) || 0
    return number.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })
  }

  const formatDateTime = (value) => {
    if (!value) return 'N/A'
    return new Date(value).toLocaleString()
  }

  const formatBatchDetailValue = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }

    if (['purchase_price', 'selling_price', 'total_cost'].includes(key)) {
      return formatCurrency(value)
    }

    if (key === 'quantity') {
      return parseFloat(value).toLocaleString()
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value)
      } catch (error) {
        return 'N/A'
      }
    }

    if (key.includes('date') || key.endsWith('_at')) {
      return formatDateTime(value)
    }

    return value
  }

  const displayTotals = useMemo(() => {
    const totalUnits = valueSummary?.total_quantity ?? summary.totalUnits
    const totalValue = valueSummary?.total_value ?? summary.totalValue
    const averagePrice =
      valueSummary?.average_purchase_price ??
      (summary.totalUnits ? summary.totalValue / summary.totalUnits : 0)

    return {
      totalUnits,
      totalValue,
      averagePrice
    }
  }, [summary.totalUnits, summary.totalValue, valueSummary])

  const handleDownloadPdf = async () => {
    if (!hasBranchContext || !branchStockRef.current || !filteredStock.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Nothing to Export',
        text: 'No branch stock data available to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we prepare the branch stock report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const doc = new jsPDF('portrait', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 14

      // Header
      doc.setFontSize(14)
      doc.setFont(undefined, 'bold')
      doc.text('Branch Stock Report', margin, 18)

      doc.setFontSize(11)
      doc.setFont(undefined, 'normal')
      const branchName = branch?.name || 'Branch'
      doc.text(`Branch: ${branchName}`, margin, 26)

      const generatedOn = new Date().toLocaleString()
      doc.text(`Generated: ${generatedOn}`, margin, 32)

      // Summary line (matches on-screen summary cards in meaning)
      const summaryLine = [
        `Products: ${summary.totalProducts}`,
        `Total Units: ${displayTotals.totalUnits.toLocaleString()}`,
        `Stock Value: ${formatCurrency(displayTotals.totalValue)}`,
        `Avg Purchase: ${formatCurrency(displayTotals.averagePrice)}`
      ].join('   |   ')

      doc.setFontSize(9)
      doc.text(summaryLine, margin, 40, { maxWidth: pageWidth - margin * 2 })

      // Main inventory table (same columns as on screen)
      const head = [
        ['Product', 'SKU', 'Batch', 'Quantity', 'Available', 'Purchase Price', 'Selling Price', 'Total Cost']
      ]

      const body = filteredStock.map(entry => [
        entry.product_name || '',
        entry.product_sku || '',
        entry.batch_number || entry.original_batch_number || '',
        (parseFloat(entry.quantity || 0) || 0).toLocaleString(),
        (parseFloat(entry.available_quantity_for_sale ?? entry.quantity ?? 0) || 0).toLocaleString(),
        formatCurrency(entry.purchase_price),
        formatCurrency(entry.selling_price),
        formatCurrency(entry.total_cost)
      ])

      autoTable(doc, {
        head,
        body,
        startY: 46,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          halign: 'left'
        },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'right' }
        },
        didDrawPage: (data) => {
          // Optional simple footer with page number
          const str = `Page ${doc.internal.getNumberOfPages()}`
          doc.setFontSize(8)
          doc.text(str, pageWidth - margin, pageHeight - 6, { align: 'right' })
        }
      })

      // Signature section (same visual meaning as on-screen "SIGNATURES")
      let y = (doc.lastAutoTable && doc.lastAutoTable.finalY + 12) || 58
      if (y > pageHeight - 60) {
        doc.addPage()
        y = 20
      }

      doc.setFontSize(9)
      doc.setFont(undefined, 'normal')
      const signaturesLabel = '-------- SIGNATURES --------'
      const labelWidth = doc.getTextWidth(signaturesLabel)
      doc.text(signaturesLabel, (pageWidth - labelWidth) / 2, y)

      y += 10

      const sectionWidth = (pageWidth - margin * 2 - 10) / 2
      const leftX = margin
      const rightX = margin + sectionWidth + 10

      const drawSignatureBlock = (x, title) => {
        doc.setFontSize(9)
        doc.setFont(undefined, 'bold')
        doc.text(title.toUpperCase(), x, y)

        doc.setFontSize(8)
        doc.setFont(undefined, 'normal')

        // Full Name line
        let lineY = y + 6
        doc.text('Full Name', x, lineY)
        lineY += 2
        doc.line(x, lineY + 3, x + sectionWidth, lineY + 3)

        // Role / Title line
        lineY += 8
        doc.text('Role / Title', x, lineY)
        lineY += 2
        doc.line(x, lineY + 3, x + sectionWidth, lineY + 3)

        // Signature line
        lineY += 8
        doc.text('Signature', x, lineY)
        lineY += 2
        doc.line(x, lineY + 4, x + sectionWidth, lineY + 4)
      }

      drawSignatureBlock(leftX, 'User 1')
      drawSignatureBlock(rightX, 'User 2')

      const fileName = `Branch_Stock_${branch?.name || 'Branch'}_${new Date()
        .toISOString()
        .split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'The branch stock report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (err) {
      console.error('Error generating branch stock PDF:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  const handleDownloadExcel = () => {
    if (!hasBranchContext || !filteredStock.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Nothing to Export',
        text: 'No branch stock data available to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      // Prepare data for Excel
      const worksheetData = []

      // Header row
      worksheetData.push([
        'Branch Stock Report',
        '',
        '',
        '',
        '',
        ''
      ])

      worksheetData.push([
        `Branch: ${branch?.name || 'Branch'}`,
        '',
        '',
        '',
        '',
        ''
      ])

      worksheetData.push([
        `Generated: ${new Date().toLocaleString()}`,
        '',
        '',
        '',
        '',
        ''
      ])

      worksheetData.push([]) // Empty row

      // Summary row
      worksheetData.push([
        'Summary',
        '',
        '',
        '',
        '',
        ''
      ])

      worksheetData.push([
        `Products: ${summary.totalProducts}`,
        `Total Units: ${displayTotals.totalUnits.toLocaleString()}`,
        `Stock Value: ${formatCurrency(displayTotals.totalValue)}`,
        `Avg Purchase: ${formatCurrency(displayTotals.averagePrice)}`,
        '',
        ''
      ])

      worksheetData.push([]) // Empty row

      // Table headers
      worksheetData.push([
        'Product',
        'SKU',
        'Batch',
        'Quantity',
        'Available',
        'Purchase Price',
        'Selling Price',
        'Total Cost'
      ])

      filteredStock.forEach(entry => {
        worksheetData.push([
          entry.product_name || '',
          entry.product_sku || '',
          entry.batch_number || entry.original_batch_number || '',
          parseFloat(entry.quantity || 0),
          parseFloat(entry.available_quantity_for_sale ?? entry.quantity ?? 0),
          parseFloat(entry.purchase_price || 0),
          parseFloat(entry.selling_price || 0),
          parseFloat(entry.total_cost || 0)
        ])
      })

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

      // Set column widths
      worksheet['!cols'] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 28 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 }
      ]

      // Style header row (row 8, 0-indexed is 7)
      const headerRowIndex = 7
      if (!worksheet['!rows']) worksheet['!rows'] = []
      worksheet['!rows'][headerRowIndex] = { hpt: 20 }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Branch Stock')

      // Generate filename
      const fileName = `Branch_Stock_${branch?.name || 'Branch'}_${new Date()
        .toISOString()
        .split('T')[0]}.xlsx`

      // Save file
      XLSX.writeFile(workbook, fileName)

      Swal.fire({
        icon: 'success',
        title: 'Excel Generated',
        text: 'The branch stock report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (err) {
      console.error('Error generating branch stock Excel:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate Excel file. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  const handleEditSellingPrice = async (entry) => {
    if (!entry?.id) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Edit Price',
        text: 'No stock entry ID found for this item.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    const result = await Swal.fire({
      title: 'Edit Selling Price',
      html: `
        <p class="mb-1">${entry.product_name || 'Product'}</p>
        <small class="text-muted">${entry.batch_number || entry.original_batch_number || 'Batch entry'}</small>
      `,
      input: 'number',
      inputValue: parseFloat(entry.selling_price || 0),
      inputAttributes: {
        min: 0,
        step: '0.01'
      },
      showCancelButton: true,
      confirmButtonText: 'Update',
      confirmButtonColor: '#3085d6',
      cancelButtonText: 'Cancel',
      preConfirm: (value) => {
        if (value === '' || value === null || Number.isNaN(Number(value))) {
          Swal.showValidationMessage('Please enter a valid selling price.')
          return false
        }

        if (Number(value) < 0) {
          Swal.showValidationMessage('Selling price cannot be negative.')
          return false
        }

        return value
      }
    })

    if (!result.isConfirmed) return

    try {
      await apiClient.put(API_ENDPOINTS.BRANCH_STOCK_DETAIL(entry.id), {
        selling_price: String(result.value)
      })

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Selling price updated successfully.',
        confirmButtonColor: '#3085d6',
        timer: 1800
      })

      await fetchBranchStock()
    } catch (err) {
      console.error('Error updating selling price:', err)
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to update selling price. Please try again.'
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: message,
        confirmButtonColor: '#d33'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Branch Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Branches</li>
            <li className="breadcrumb-item">
              <button className="btn btn-link p-0 border-0 align-baseline" onClick={handleGoBack}>
                Manage Branches
              </button>
            </li>
            <li className="breadcrumb-item active">Branch Stock</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">{branch?.name || 'Branch Stock'}</h4>
              <p className="text-muted mb-0">
                {branch?.warehouse_name ? `Linked warehouse: ${branch.warehouse_name}` : 'Select a branch to view its inventory.'}
              </p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={handleGoBack}>
                <i className="bi bi-arrow-left me-1"></i>
                Back
              </button>
              <button className="btn btn-outline-primary" onClick={fetchBranchStock} disabled={loading || !hasBranchContext}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDownloadPdf}
                disabled={loading || !hasBranchContext || !filteredStock.length}
              >
                <i className="bi bi-file-earmark-pdf me-1"></i>
                Download PDF
              </button>
              <button
                className="btn btn-success"
                onClick={handleDownloadExcel}
                disabled={loading || !hasBranchContext || !filteredStock.length}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Download Excel
              </button>
            </div>
          </div>

          <div className="col-12 mb-2">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '8px 16px',
                borderRadius: '24px',
                color: 'white',
                minWidth: '200px',
                boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)'
              }}>
                <i className="bi bi-box-seam" style={{ fontSize: '20px' }}></i>
                <div>
                  <small className="text-uppercase" style={{ letterSpacing: '0.5px', opacity: 0.85 }}>Products</small>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{summary.totalProducts}</div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                padding: '8px 16px',
                borderRadius: '24px',
                color: 'white',
                minWidth: '200px',
                boxShadow: '0 3px 12px rgba(17, 153, 142, 0.3)'
              }}>
                <i className="bi bi-stack" style={{ fontSize: '20px' }}></i>
                <div>
                  <small className="text-uppercase" style={{ letterSpacing: '0.5px', opacity: 0.85 }}>Total Units</small>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{displayTotals.totalUnits.toLocaleString()}</div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{
                background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                padding: '8px 16px',
                borderRadius: '24px',
                color: 'white',
                minWidth: '200px',
                boxShadow: '0 3px 12px rgba(247, 151, 30, 0.3)'
              }}>
                <i className="bi bi-cash-coin" style={{ fontSize: '20px' }}></i>
                <div>
                  <small className="text-uppercase" style={{ letterSpacing: '0.5px', opacity: 0.85 }}>Stock Value</small>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{formatCurrency(displayTotals.totalValue)}</div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{
                background: 'linear-gradient(135deg, #0f2027 0%, #2c5364 100%)',
                padding: '8px 16px',
                borderRadius: '24px',
                color: 'white',
                minWidth: '200px',
                boxShadow: '0 3px 12px rgba(15, 32, 39, 0.35)'
              }}>
                <i className="bi bi-bar-chart-line" style={{ fontSize: '20px' }}></i>
                <div>
                  <small className="text-uppercase" style={{ letterSpacing: '0.5px', opacity: 0.85 }}>Avg Purchase</small>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{formatCurrency(displayTotals.averagePrice)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mt-4" ref={branchStockRef}>
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                  <div>
                    <h5 className="card-title mb-1">Branch Inventory</h5>
                    {lastUpdated && (
                      <small className="text-muted">
                        Last updated: {lastUpdated.toLocaleString()}
                      </small>
                    )}
                  </div>
                  <div className="mt-3 mt-md-0" style={{ minWidth: '250px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by product, SKU, batch"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                {!hasBranchContext ? (
                  <div className="text-center py-5">
                    <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-3 text-muted">{error}</p>
                    <button className="btn btn-primary" onClick={handleGoBack}>
                      Go Back
                    </button>
                  </div>
                ) : loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Fetching stock for this branch...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <i className="bi bi-wifi-off text-danger" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-3 text-muted">{error}</p>
                    <button className="btn btn-outline-primary" onClick={fetchBranchStock}>
                      Retry
                    </button>
                  </div>
                ) : filteredStock.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-box text-muted" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-3 text-muted">No stock records found for this branch.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Batch</th>
                          <th className="text-end">Quantity</th>
                          <th className="text-end">Available</th>
                          <th className="text-end">Purchase Price</th>
                          <th className="text-end">Selling Price</th>
                          <th className="text-end">Total Cost</th>
                          <th style={{ width: '140px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedStock.map(entry => (
                          <tr key={entry.id}>
                            <td className="fw-semibold">{entry.product_name}</td>
                            <td><span className="badge bg-light text-dark">{entry.product_sku}</span></td>
                            <td>
                              <span className="text-muted small">
                                {entry.batch_number || entry.original_batch_number || '—'}
                              </span>
                            </td>
                            <td className="text-end">{parseFloat(entry.quantity || 0).toLocaleString()}</td>
                            <td className="text-end">
                              {parseFloat(entry.available_quantity_for_sale ?? entry.quantity ?? 0).toLocaleString()}
                            </td>
                            <td className="text-end">{formatCurrency(entry.purchase_price)}</td>
                            <td className="text-end">{formatCurrency(entry.selling_price)}</td>
                            <td className="text-end">{formatCurrency(entry.total_cost)}</td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-warning me-2"
                                onClick={() => handleEditSellingPrice(entry)}
                                title="Edit Selling Price"
                              >
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => showEntryDetails(entry)}
                                title="View Details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="stock entries"
                    />
                  </div>
                )}

                {hasBranchContext && filteredStock.length > 0 && (
                  <div className="mt-4 pt-3 border-top border-2 border-dashed">
                    <div className="text-center mb-3">
                      <span style={{ letterSpacing: '4px', color: '#6c757d' }}>-------- SIGNATURES --------</span>
                    </div>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="p-3 rounded-3 border">
                          <h6 className="mb-3 text-muted text-uppercase" style={{ letterSpacing: '0.08em' }}>
                            User 1
                          </h6>
                          <div className="mb-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Full Name</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '24px'
                                }}
                              ></div>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Role / Title</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '24px'
                                }}
                              ></div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Signature</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '32px'
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 rounded-3 border">
                          <h6 className="mb-3 text-muted text-uppercase" style={{ letterSpacing: '0.08em' }}>
                            User 2
                          </h6>
                          <div className="mb-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Full Name</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '24px'
                                }}
                              ></div>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Role / Title</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '24px'
                                }}
                              ></div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <div className="d-flex flex-column">
                              <span className="small text-muted mb-1">Signature</span>
                              <div
                                style={{
                                  borderBottom: '1px dotted #adb5bd',
                                  height: '32px'
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default BranchStock



